import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCode,
  faFileCode,
  faFileImport,
  faRoute,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import authProfileService from '../../services/authProfileService';

/**
 * AuthProfileExtractorModal - Extract tokens from various sources
 * Tabs: HTML, JSON, HAR, Multi-step
 */
const AuthProfileExtractorModal = ({ onClose, onTokenExtracted, projectId, existingProfile = null }) => {
  const [activeTab, setActiveTab] = useState('html');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [extractedTokens, setExtractedTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);

  // HTML extraction state
  const [htmlSource, setHtmlSource] = useState('');

  // JSON extraction state
  const [jsonSource, setJsonSource] = useState('');

  // HAR extraction state
  const [harFile, setHarFile] = useState(null);
  const [harFileName, setHarFileName] = useState('');

  // Multi-step extraction state (simplified for now)
  const [multiStepSteps, setMultiStepSteps] = useState([
    {
      stepNumber: 1,
      method: 'POST',
      url: '',
      headers: {},
      body: '',
      extractFrom: 'response_body',
      extractField: 'token'
    }
  ]);

  const tabs = [
    { id: 'html', label: 'HTML Source', icon: faCode },
    { id: 'json', label: 'JSON Response', icon: faFileCode },
    { id: 'har', label: 'HAR File', icon: faFileImport },
    { id: 'multi', label: 'Multi-step', icon: faRoute }
  ];

  // Extract from HTML
  const handleExtractFromHtml = async () => {
    if (!htmlSource.trim()) {
      setError('Please paste HTML source code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await authProfileService.extractFromHtml(htmlSource);

      if (result.success && result.tokens.length > 0) {
        setExtractedTokens(result.tokens);
        if (result.tokens.length === 1) {
          setSelectedToken(result.tokens[0]);
        }
      } else {
        setError(result.message || 'No tokens found in HTML');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to extract tokens from HTML');
    } finally {
      setLoading(false);
    }
  };

  // Extract from JSON
  const handleExtractFromJson = async () => {
    if (!jsonSource.trim()) {
      setError('Please paste JSON response');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await authProfileService.extractFromJson(jsonSource);

      if (result.success && result.tokens.length > 0) {
        setExtractedTokens(result.tokens);
        if (result.tokens.length === 1) {
          setSelectedToken(result.tokens[0]);
        }
      } else {
        setError(result.message || 'No tokens found in JSON');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to extract tokens from JSON');
    } finally {
      setLoading(false);
    }
  };

  // Extract from HAR
  const handleExtractFromHar = async () => {
    if (!harFile) {
      setError('Please select a HAR file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await authProfileService.extractFromHar(harFile);

      if (result.success && result.tokens.length > 0) {
        setExtractedTokens(result.tokens);
        if (result.tokens.length === 1) {
          setSelectedToken(result.tokens[0]);
        }
      } else {
        setError(result.message || 'No tokens found in HAR file');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to extract tokens from HAR');
    } finally {
      setLoading(false);
    }
  };

  // Handle HAR file selection
  const handleHarFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHarFile(file);
      setHarFileName(file.name);
    }
  };

  // Apply selected token
  const handleApplyToken = async () => {
    if (!selectedToken) {
      setError('Please select a token first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const tokenWithSource = {
        ...selectedToken,
        source: activeTab
      };

      onTokenExtracted(tokenWithSource);
      onClose();
    } catch (err) {
      setError('Failed to apply token');
    } finally {
      setLoading(false);
    }
  };

  // Get confidence badge color
  const getConfidenceBadgeColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'bg-green-100 text-green-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Get auth type badge color
  const getAuthTypeBadgeColor = (authType) => {
    switch (authType) {
      case 'jwt':
      case 'bearer':
        return 'bg-blue-100 text-blue-700';
      case 'cookie':
        return 'bg-purple-100 text-purple-700';
      case 'api_key':
        return 'bg-orange-100 text-orange-700';
      case 'csrf':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Extract Authentication Token</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setExtractedTokens([]);
                setSelectedToken(null);
                setError(null);
              }}
              className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* HTML Tab */}
          {activeTab === 'html' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  HTML Source Code
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="ml-2 text-gray-400"
                    title="Paste HTML source from login pages, API responses, or any page containing tokens"
                  />
                </label>
                <textarea
                  value={htmlSource}
                  onChange={(e) => setHtmlSource(e.target.value)}
                  placeholder="<html>&#10;  <head>&#10;    <meta name='csrf-token' content='abc123...' />&#10;  </head>&#10;  <body>...</body>&#10;</html>"
                  className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Extracts tokens from: meta tags, hidden inputs, script variables, data attributes
                </p>
              </div>

              <button
                onClick={handleExtractFromHtml}
                disabled={loading || !htmlSource.trim()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Extracting...
                  </>
                ) : (
                  <>Extract Tokens</>
                )}
              </button>
            </div>
          )}

          {/* JSON Tab */}
          {activeTab === 'json' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  JSON Response
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="ml-2 text-gray-400"
                    title="Paste JSON from login API responses or any JSON containing authentication tokens"
                  />
                </label>
                <textarea
                  value={jsonSource}
                  onChange={(e) => setJsonSource(e.target.value)}
                  placeholder='{&#10;  "success": true,&#10;  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",&#10;  "refreshToken": "def456..."&#10;}'
                  className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Extracts tokens from common field names: token, accessToken, jwt, bearer, apiKey, etc.
                </p>
              </div>

              <button
                onClick={handleExtractFromJson}
                disabled={loading || !jsonSource.trim()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Extracting...
                  </>
                ) : (
                  <>Extract Tokens</>
                )}
              </button>
            </div>
          )}

          {/* HAR Tab */}
          {activeTab === 'har' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  HAR File
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="ml-2 text-gray-400"
                    title="Upload HAR file from browser DevTools (Network tab > right-click > Save all as HAR)"
                  />
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".har"
                    onChange={handleHarFileChange}
                    className="hidden"
                    id="har-file-input"
                    disabled={loading}
                  />
                  <label htmlFor="har-file-input" className="cursor-pointer">
                    <FontAwesomeIcon icon={faFileImport} className="text-4xl text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      {harFileName ? (
                        <span className="font-semibold">{harFileName}</span>
                      ) : (
                        <>Click to select HAR file</>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Max size: 10MB
                    </p>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Extracts tokens from: cookies, Authorization headers, Set-Cookie headers, JSON responses
                </p>
              </div>

              <button
                onClick={handleExtractFromHar}
                disabled={loading || !harFile}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Extracting...
                  </>
                ) : (
                  <>Extract Tokens</>
                )}
              </button>
            </div>
          )}

          {/* Multi-step Tab (Placeholder) */}
          {activeTab === 'multi' && (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-900 mb-1">
                      Multi-step Extraction (Coming Soon)
                    </p>
                    <p className="text-xs text-yellow-700">
                      This feature will allow you to automate login flows with multiple steps:
                    </p>
                    <ul className="text-xs text-yellow-700 mt-2 list-disc list-inside space-y-1">
                      <li>Step 1: POST /api/login with credentials</li>
                      <li>Step 2: Extract session cookie from Set-Cookie</li>
                      <li>Step 3: GET /api/csrf to fetch CSRF token</li>
                      <li>Step 4: Extract token from JSON response</li>
                    </ul>
                    <p className="text-xs text-yellow-700 mt-2">
                      For now, use the Proxy or HAR extraction methods to capture tokens from completed login flows.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Extracted Tokens List */}
          {extractedTokens.length > 0 && (
            <div className="mt-6 border-t pt-6">
              <h3 className="text-sm font-bold text-gray-900 mb-3">
                Found {extractedTokens.length} Token{extractedTokens.length !== 1 ? 's' : ''}
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {extractedTokens.map((token, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedToken(token)}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedToken === token
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{token.name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getAuthTypeBadgeColor(token.authType)}`}>
                          {token.authType}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getConfidenceBadgeColor(token.confidence)}`}>
                          {token.confidence}
                        </span>
                      </div>
                      {selectedToken === token && (
                        <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600" />
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mb-1">
                      <span className="font-semibold">Location:</span> {token.location}
                    </div>
                    <div className="text-xs text-gray-900 font-mono bg-gray-100 p-2 rounded break-all">
                      {token.value.length > 100 ? `${token.value.substring(0, 100)}...` : token.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyToken}
            disabled={!selectedToken || loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {existingProfile ? 'Apply to Profile' : 'Create Profile'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthProfileExtractorModal;
